/**
 * Provider Types for New Backend
 *
 * Aligned with the backend ProfileProvider model in accounts/models/profiles.py
 * and the REST endpoints in accounts/views.py:
 *   - POST   /api/providers/create/
 *   - PUT    /api/providers/<uuid>/update/
 *   - DELETE /api/providers/<uuid>/delete/
 */

// ============================================================================
// Provider Type Choices (from backend model)
// ============================================================================

// Seeded provider type codes from the backend lookup (accounts migration 0026).
// `provider_type` is an affiliation by intent (partner / wtd_fleet / flex) but may
// hold a clinical code on legacy or explicitly-onboarded records — treat it as an
// opaque code. New codes can be added by ops; the canonical live list comes from the
// `providerTypes` GraphQL query (see useProviderTypes). This list backs our local i18n.
export const PROVIDER_TYPES = [
  'nurse',
  'lpn',
  'cna',
  'therapist',
  'physician',
  'specialist',
  'technician',
  'aide',
  'companion',
  'partner',
  'wtd_fleet',
  'flex',
  'other',
] as const

export type ProviderType = (typeof PROVIDER_TYPES)[number]

export const PROVIDER_TYPE_LABELS: Record<ProviderType, string> = {
  nurse: 'Nurse',
  lpn: 'Licensed Practical Nurse (LPN)',
  cna: 'Certified Nursing Assistant (CNA)',
  therapist: 'Therapist',
  physician: 'Physician',
  specialist: 'Specialist',
  technician: 'Technician',
  aide: 'Aide',
  companion: 'Companion',
  partner: 'Partner',
  wtd_fleet: 'WTD Fleet',
  flex: 'Flex',
  other: 'Other',
}

// ============================================================================
// Availability Status (FSM states from backend)
// ============================================================================

export const AVAILABILITY_STATUSES = [
  'available',
  'busy',
  'unavailable',
  'on_leave',
] as const

export type AvailabilityStatus = (typeof AVAILABILITY_STATUSES)[number]

export const AVAILABILITY_STATUS_LABELS: Record<AvailabilityStatus, string> = {
  available: 'Available',
  busy: 'Busy',
  unavailable: 'Unavailable',
  on_leave: 'On Leave',
}

// ============================================================================
// Create Provider Input
// ============================================================================

export interface CreateProviderInput {
  /** Required: Provider's first name */
  first_name: string
  /** Required: Provider's last name */
  last_name: string
  /** Required: Phone number (E.164 format, must be unique) */
  phone_number: string
  /** Required: Type of provider */
  provider_type: ProviderType
  /** Optional: Email address */
  email?: string
  /** Optional: List of specializations */
  specializations?: string[]
  /** Optional: Professional license number */
  license_number?: string
  /** Optional: State where license was issued */
  license_state?: string
  /** Optional: License expiration date (YYYY-MM-DD) */
  license_expiry_date?: string
  /** Optional: List of certifications */
  certifications?: string[]
  /** Optional: Years of professional experience */
  years_of_experience?: number
  /** Optional: Maximum travel distance in km */
  max_travel_distance?: number
}

// ============================================================================
// Update Provider Input
// ============================================================================

export interface UpdateProviderInput {
  /** Optional: Full name (backend splits into first/last) */
  name?: string
  /** Optional: Phone number (E.164 format) */
  phone_number?: string
  /** Optional: Entity/organization ID */
  entity_id?: string
  /** Optional: Availability status */
  availability_status?: AvailabilityStatus
  /** Optional: Maximum travel distance in km */
  max_travel_distance?: number
}

// ============================================================================
// API Response Types
// ============================================================================

export interface CreateProviderResponse {
  success: boolean
  message?: string
  user?: {
    id: string
    phone_number: string
    email: string | null
    first_name: string
    last_name: string
  }
  profile?: {
    id: string
    profile_type: string
    status: string
    display_name: string
  }
  provider?: {
    id: string
    provider_type: ProviderType
    specializations: string[]
    license_number: string
    license_state: string
    license_expiry_date: string | null
    years_of_experience: number
    availability_status: AvailabilityStatus
  }
  temporary_password?: string
  note?: string
  // Error fields
  error_code?: string
  details?: Record<string, unknown>
}

export interface UpdateProviderResponse {
  success: boolean
  message?: string
  provider?: {
    id: string
    provider_type: ProviderType
    availability_status: AvailabilityStatus
    entity_id: string | null
    company_id: string | null
    max_travel_distance: number | null
  }
  error_code?: string
}

export interface DeleteProviderResponse {
  success: boolean
  message?: string
  error_code?: string
}
